// @owners { team: patients-team }
import {
  ActionSheetContext,
  Body,
  Button,
  ButtonGroup,
  Column,
  LgPadding,
  Link,
  LoadingEmptyState,
  MdSpacing,
  Row,
  SmPadding,
} from '@alto/design-system';
import { CardFinderActionSheet, CashPayInsurance, EmptyInsurance } from '@alto/features';
import React, { useContext, useState } from 'react';
import { deselectInsurance } from '~shared/actions/insurances';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers, updateCashPay } from '~shared/actions/users';
import { getUserCashPayOnly } from '~shared/features/users/selectors/getUserCashPayOnly';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getResolution } from '~shared/lib/analytics/src/events/onboarding/getInsuranceCompleted';
import {
  useGetCardFinderInsurances,
  useGetInsurances,
  useGetInsurancesByUserID,
} from '~shared/queries/query-keys/insurances';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { OnboardingCommsPreferencesActionSheet } from './OrganicOnboarding';
import { Insurance } from '~web/features/insurances/components/Insurance';
import { InsuranceFormContainer } from '~web/features/insurances/components/InsuranceFormContainer';
import { InsuranceList } from '~web/features/insurances/components/InsuranceList';
import { InsurancePhotoForm } from '~web/features/insurances/components/InsurancePhotoForm';
import cardFinder from '~web/images/insuranceCard@1x.png';

export const OnboardingInsuranceForm = () => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatchShared();
  const userID = useSelectorShared(getCurrentUserID);
  const { hasActiveInsurances, hasInsurances } = useGetInsurancesByUserID({ userID });
  const isCashPayOnly = useSelectorShared((state) =>
    getUserCashPayOnly(state, {
      userID,
    }),
  );
  const isNeedsInsurance = !isCashPayOnly && !hasInsurances;
  const resolution = useSelectorShared(getResolution);
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  const [isAddingInsurance, setIsAddingInsurance] = useState(false); // manual entry
  const [isUploadingInsurance, setIsUploadingInsurance] = useState(false); // photo upload

  const { insurances, isPending: loading } = useGetInsurances();
  const { insurances: cardFinderInsurances, hasCardFinderInsurance } = useGetCardFinderInsurances();

  const handleCloseAll = () => {
    setIsAddingInsurance(false);
    setIsUploadingInsurance(false);
  };

  const handleAddInsurance = () => {
    dispatch(deselectInsurance());
    setIsAddingInsurance(true);
    setIsUploadingInsurance(false);
  };

  const handleEditInsurance = () => {
    setIsAddingInsurance(true);
    setIsUploadingInsurance(false);
  };

  const handleUploadInsurance = () => {
    setIsAddingInsurance(false);
    setIsUploadingInsurance(true);
  };

  const handleNext = () => {
    trackEvent({
      event: EVENTS.ONBOARDING__INSURANCE_STEP_COMPLETED,
      params: {
        'cardfinder insurance present': hasCardFinderInsurance,
        resolution,
      },
    });
    setActiveActionSheet(<OnboardingCommsPreferencesActionSheet />, { dismissible: false });
  };

  const handleSelectCashPay = async () => {
    if (!userID) return;
    const { success } = await dispatch(updateCashPay(userID, true));
    if (success) {
      trackEvent({
        event: EVENTS.CASH_PAY_ONLY_SET,
        params: {
          source: 'onboarding',
          'new value': true,
        },
      });
      // eslint-disable-next-line import/no-deprecated
      dispatch(fetchUsers());
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <>
          <LoadingEmptyState />
          <MdSpacing />
          <Body>Loading your insurances...</Body>
        </>
      );
    }

    if (isNeedsInsurance) {
      return (
        <EmptyInsurance
          onSkip={handleNext}
          onAddInsurance={handleAddInsurance}
          onSelectCashPay={handleSelectCashPay}
        />
      );
    }

    if (isCashPayOnly) {
      return (
        <CashPayInsurance
          hasActiveInsurances={hasActiveInsurances}
          insurances={insurances}
          onAddInsurance={handleAddInsurance}
          source="onboarding"
        />
      );
    }

    let insuranceContent = (
      <InsuranceList
        hasActiveInsurances={hasActiveInsurances}
        insurances={insurances}
        source="onboarding"
        onEditInsurance={handleEditInsurance}
      />
    );

    const firstCardFinderInsurance = cardFinderInsurances[0];

    if (firstCardFinderInsurance && insurances.length === 1) {
      insuranceContent = (
        <Column gap="SM">
          <SmPadding>
            <Row
              spaceBetween
              centerVertically
            >
              <Column>
                <Body>We found your insurance!</Body>
                <Link
                  onPress={() => {
                    setActiveActionSheet(<CardFinderActionSheet />);
                  }}
                >
                  How does it work?
                </Link>
              </Column>
              <img
                width="44px"
                height="44px"
                alt="cardfinder"
                src={cardFinder}
              />
            </Row>
          </SmPadding>
          <SmPadding>
            <Insurance
              hideActions
              insurance={firstCardFinderInsurance}
              source="onboarding"
            />
          </SmPadding>
        </Column>
      );
    }

    return (
      <Column gap="MD">
        <Body>
          Have additional insurance or a prescription savings card? Add them all, so we can get you the best pricing.
        </Body>
        {insuranceContent}
      </Column>
    );
  };

  if (isAddingInsurance) {
    return (
      <InsuranceFormContainer
        onCancel={handleCloseAll}
        onShowPhotoForm={handleUploadInsurance}
      />
    );
  }

  if (isUploadingInsurance) {
    return (
      <InsurancePhotoForm
        onCancel={handleAddInsurance}
        onSave={handleCloseAll}
        source="onboarding"
      />
    );
  }

  return (
    <LgPadding>
      {renderBody()}
      <MdSpacing />
      {hasInsurances || isCashPayOnly ? (
        <ButtonGroup
          justifyButtons="right"
          inline
          rowReversed={hasInsurances}
          buttons={[
            <Button
              key="add-benefit-button"
              label={isCashPayOnly ? 'Add benefits' : 'Add another benefit'}
              type={isCashPayOnly || hasInsurances ? 'secondary' : 'primary'}
              onPress={handleAddInsurance}
            />,
            <Button
              key="next-button"
              label="Next"
              accessibilityLabel="Press to save and continue"
              type={isCashPayOnly || hasInsurances ? 'primary' : 'tertiary'}
              onPress={handleNext}
            />,
          ]}
        />
      ) : null}
    </LgPadding>
  );
};
